<template>
    <div
        ref="rootRef"
        class="tw:relative"
        data-testid="header-cart"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <button
            type="button"
            class="admin-header-cart-hover tw:group tw:relative tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:p-2 tw:text-slate-800 tw:transition tw:hover:border-slate-300 tw:hover:bg-slate-50"
            aria-label="Carrito de taquilla"
            :aria-expanded="open ? 'true' : 'false'"
            data-testid="header-cart-toggle"
            @click="toggle"
        >
            <i class="admin-header-icon fas fa-shopping-cart tw:text-[15px]" aria-hidden="true"></i>
            <span
                v-if="totals.productos > 0"
                :key="totals.productos"
                class="tw:absolute tw:-top-1.5 tw:-right-1.5 tw:flex tw:items-center tw:justify-center tw:min-w-[16px] tw:h-4 tw:px-1 tw:rounded-full tw:text-[10px] tw:font-bold admin-header-badge-pop"
                :style="{ backgroundColor: 'var(--admin-club-color)', color: 'var(--admin-club-fg)' }"
                data-testid="header-cart-badge"
            >{{ totals.productos }}</span>
        </button>

        <div
            v-show="open"
            class="tw:fixed tw:left-4 tw:right-4 tw:top-[72px] tw:z-50 tw:w-auto tw:max-w-none tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:shadow-xl tw:text-slate-900 tw:md:absolute tw:md:left-auto tw:md:right-0 tw:md:top-full tw:md:mt-2 tw:md:w-96 tw:md:max-w-[calc(100vw-2rem)]"
            data-testid="header-cart-panel"
        >
            <div class="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-b tw:border-slate-100 tw:px-4 tw:py-3">
                <span class="tw:text-sm tw:font-semibold">{{ $t('carrito.mi_carrito') }}</span>
                <span v-if="items.length > 0 && remaining !== null" class="tw:text-[11px] tw:font-medium tw:text-slate-500" data-testid="header-cart-timer">
                    {{ remaining > 0 ? $t('carrito.entradas_disponibles') + ': ' + formatRemaining(remaining) : $t('carrito.tiempo_agotado') }}
                </span>
            </div>

            <div class="tw:max-h-[340px] tw:overflow-y-auto tw:p-3">
                <div v-if="items.length === 0" class="tw:py-6 tw:text-center tw:text-sm tw:text-slate-500" data-testid="header-cart-empty">
                    {{ $t('carrito.no_hay_entradas') }}
                </div>

                <template v-else>
                    <div
                        v-for="(evento, index) in items"
                        :key="index"
                        class="tw:mb-3 tw:rounded-lg tw:border tw:border-slate-100 tw:bg-slate-50/60 tw:p-3 last:tw:mb-0"
                    >
                        <div class="tw:truncate tw:text-xs tw:font-semibold tw:text-slate-800">{{ evento.evento }}</div>
                        <div class="tw:mb-2 tw:text-[11px] tw:text-slate-500">{{ evento.fecha }}</div>

                        <div
                            v-for="(sub, subIndex) in evento.items"
                            :key="subIndex"
                            class="tw:flex tw:items-start tw:justify-between tw:gap-2 tw:border-t tw:border-slate-100 tw:py-1.5 first:tw:border-t-0"
                        >
                            <div class="tw:min-w-0 tw:text-[11px] tw:leading-snug tw:text-slate-600">
                                <div v-if="sub.entrada && sub.entrada.descripcion" class="tw:truncate">
                                    <strong>{{ $t('carrito.entrada') }}:</strong> {{ sub.entrada.descripcion }}
                                </div>
                                <div v-if="sub.asiento" class="tw:truncate">
                                    {{ (sub.asiento.region_name || '') + ' | ' + $t('carrito.fila') + ': ' + sub.asiento.row + ' | ' + $t('carrito.asiento') + ': ' + sub.asiento.seat }}
                                </div>
                                <div v-if="sub.plaza" class="tw:truncate">
                                    {{ (sub.plaza.region_name || '') + ' | ' + $t('carrito.fila') + ': ' + sub.plaza.fila + ' | ' + $t('carrito.asiento') + ': ' + sub.plaza.butaca }}
                                </div>
                                <div class="tw:text-slate-400">
                                    {{ $t('carrito.cantidad') }}: {{ sub.units }} · {{ $t('carrito.precio') }}: {{ $n(sub.precio_unidad, 'currency') }}
                                </div>
                            </div>
                            <button
                                type="button"
                                class="tw:shrink-0 tw:rounded-md tw:p-1.5 tw:text-slate-400 tw:transition-colors tw:hover:bg-rose-50 tw:hover:text-rose-500"
                                :data-testid="'header-cart-delete-' + sub.id"
                                aria-label="Eliminar entrada"
                                @click="deleteItem(sub.id)"
                            >
                                <i class="fas fa-trash-alt tw:text-xs" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </template>
            </div>

            <div v-if="items.length > 0" class="tw:space-y-1 tw:border-t tw:border-slate-100 tw:p-3">
                <div class="tw:flex tw:justify-between tw:text-xs tw:text-slate-600">
                    <span>{{ $t('carrito.numero_entradas') }}</span>
                    <span data-testid="header-cart-total-productos">{{ totals.productos }}</span>
                </div>
                <div class="tw:flex tw:justify-between tw:text-xs tw:text-slate-600">
                    <span>{{ $t('carrito.gastos_gestion') }}</span>
                    <span>{{ $n(totals.gastos, 'currency') }}</span>
                </div>
                <div class="tw:flex tw:justify-between tw:text-sm tw:font-bold tw:text-slate-900">
                    <span>{{ $t('carrito.total') }}</span>
                    <span>{{ $n(totals.total, 'currency') }}</span>
                </div>
                <button
                    type="button"
                    class="tw:mt-2 tw:w-full tw:rounded-lg tw:px-4 tw:py-2 tw:text-xs tw:font-semibold tw:transition tw:hover:opacity-90"
                    :style="{ backgroundColor: 'var(--admin-club-color)', color: 'var(--admin-club-fg)' }"
                    data-testid="header-cart-checkout"
                    @click="goToCart"
                >{{ $t('carrito.ver_carrito') }}</button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import { useRouter } from '@/composables/common/router/useRouter';
    import { useAdminCart, formatRemaining } from '@/composables/admin/layout/useAdminCart';
    import { useHeaderDropdown } from '@/composables/admin/layout/useHeaderDropdown';

    const router = useRouter();

    const { items, totals, remaining, deleteItem } = useAdminCart();

    const rootRef = ref(null);
    const { open, toggle, close, onMouseEnter, onMouseLeave } = useHeaderDropdown(rootRef, { hover: true });

    function goToCart() {
        close();
        router.push({ name: 'back.carrito' });
    }
</script>

<style scoped>
    /*
     * Carrito: scale 1.1 + dos saltos de 5px (mismo gesto que el icono de
     * referencia: y [0, -5, 0] repetido, ~0.9s).
     */
    .admin-header-cart-hover:hover .admin-header-icon {
        animation: adminCartBounce 0.9s ease-in-out;
    }

    @keyframes adminCartBounce {
        0% {
            transform: translateY(0) scale(1);
        }
        10% {
            transform: translateY(0) scale(1.1);
        }
        25% {
            transform: translateY(-1.5px) scale(1.1);
        }
        50% {
            transform: translateY(0) scale(1.1);
        }
        75% {
            transform: translateY(-1.5px) scale(1.1);
        }
        100% {
            transform: translateY(0) scale(1.1);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .admin-header-cart-hover:hover .admin-header-icon {
            animation: none;
        }
    }

    .admin-header-badge-pop {
        animation: adminHeaderBadgePop 0.25s ease;
    }

    @keyframes adminHeaderBadgePop {
        0% {
            transform: scale(0.6);
        }
        100% {
            transform: scale(1);
        }
    }
</style>
